import SharedMessagesV2 from "@/components/shared/MessagesV2";
import { useUserRegion } from "@/hooks/useUserRegion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSelector } from "@/components/ui/language-selector";
import { auth, db } from "@/lib/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  orderBy
} from "firebase/firestore";
import { SellerSidebar } from "@/components/layout/SellerSidebar";
import { SellerContactRequests } from "@/components/dashboard/ContactRequestsTracker";
import SecureDocumentsSection from "@/components/seller/SecureDocumentsSection";
import UpdateImpactDataDialog from "@/components/seller/UpdateImpactDataDialog";
import { useContactRequestNotifications } from "@/hooks/useContactRequestNotifications";
import { contactRequestsApi } from "@/services/api/contact-requests.api";
import { DashboardHeader } from "@/components/layout/DashboardHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import {
  FileText,
  Users,
  Building2,
  DollarSign,
  MessageSquare,
  Calendar,
  Video,
  Download,
  Check,
  X,
  Eye,
  Clock,
  Send,
  Bell,
  UserCheck,
  Shield,
  Upload,
  Edit,
  Plus,
  Trash2
} from "lucide-react";
import { SubmitDealForm } from "@/components/submit-deal/SubmitDealForm";
import CIMApprovalBanner from "@/components/seller/CIMApprovalBanner";
import { ConsultantsTab, PartnersTab, ExpertsTab, CalendarTab, type Consultant } from "@/components/dashboard/shared";
import { CompactCalendarWidget } from "@/components/dashboard/CompactCalendarWidget";
import { OnboardingGuide } from '@/components/onboarding/OnboardingGuide';
import { postApi } from '@/lib/api';

// Translations
const translations = {
  en: {
    title: "Seller Dashboard",
    subtitle: "Manage your business listings and buyer interactions",
    activeListings: "Active Listings",
    contactRequests: "Contact Requests",
    loiReceived: "LOI Received",
    totalViews: "Total Views",
    overview: "Overview",
    myDeals: "My Deals",
    messages: "Messages",
    documents: "Documents",
    consultants: "Consultants",
    partners: "Partners",
    ourExperts: "Our Experts",
    pendingContactRequests: "Pending Contact Requests",
    buyersWaiting: "Buyers waiting for your response",
    noPendingRequests: "No pending requests",
    recentLOI: "Recent LOI Submissions",
    newLOI: "New letters of intent",
    noNewLOI: "No new LOI submissions",
    quickActions: "Quick Actions",
    listNewBusiness: "List New Business",
    manageListings: "Manage Listings",
    viewMessages: "View Messages",
    getExpertHelp: "Get Expert Help",
    myBusinessListings: "My Business Listings",
    manageDeals: "Manage your submitted deals",
    submitNewDeal: "Submit New Deal",
    noListingsYet: "No listings yet",
    submitFirstDeal: "Submit Your First Deal",
    askingPrice: "Asking Price",
    views: "Views",
    interestedBuyers: "Interested Buyers",
    edit: "Edit",
    viewPublicListing: "View Public Listing",
    deactivate: "Deactivate",
    messagesContactRequests: "Messages & Contact Requests",
    manageBuyerComms: "Manage buyer communications",
    noMessagesYet: "No messages yet",
    pendingRequests: "Pending Requests",
    acceptedRequests: "Accepted Requests",
    rejectedRequests: "Rejected Requests",
    viewProfile: "View Profile",
    accept: "Accept",
    reject: "Reject",
    scheduled: "Scheduled",
    joinMeeting: "Join Meeting",
    rejected: "Rejected",
    documentsLOI: "Documents & LOI",
    allDocuments: "All your business documents and received LOI",
    lettersOfIntent: "Letters of Intent",
    meetingRecordings: "Meeting Recordings",
    otherDocuments: "Other Documents",
    noLOIReceived: "No LOI received yet",
    loiFrom: "LOI from",
    deal: "Deal",
    offeredPrice: "Offered Price",
    receivedDate: "Received Date",
    downloadLOI: "Download LOI",
    viewBuyer: "View Buyer",
    counterOffer: "Counter Offer",
    noMeetingRecordings: "No meeting recordings yet",
    watchRecording: "Watch Recording",
    viewTranscript: "View Transcript",
    download: "Download",
    noOtherDocuments: "No other documents",
    myConsultants: "My Consultants",
    privateAdvisors: "Your private advisors and consultants",
    noConsultantsAdded: "No consultants added",
    addConsultant: "Add Consultant",
    myPartners: "My Partners",
    internalExternal: "Internal and external business partners",
    noPartnersAdded: "No partners added",
    addPartner: "Add Partner",
    internalPartners: "Internal Partners",
    externalPartners: "External Partners",
    acquizExperts: "AcquizGroup's verified internal experts ready to assist you",
    noExpertsAvailable: "No experts available at the moment",
    rate: "Rate",
    availability: "Availability",
    rating: "Rating",
    reviews: "Reviews",
    bookConsultation: "Book Consultation",
    message: "Message",
    expertCategories: "Expert Categories",
    browseExperts: "Browse experts by specialty",
    businessLaw: "Business Law",
    immigrationLaw: "Immigration Law",
    cpaServices: "CPA Services",
    notaryPublic: "Notary Public",
    taxAdvisory: "Tax Advisory",
    experts: "Experts",
    partnersInvolved: "Partners Involved",
    interestedIn: "Interested in",
    loading: "Loading dashboard...",
    new: "New",
    respond: "Respond",
    contact: "Contact",
    acceptShareCalendar: "Accept & Share Calendar",
    uploadedAt: "Uploaded"
  },
  fr: {
    title: "Tableau de bord vendeur",
    subtitle: "Gérez vos annonces et interactions avec les acheteurs",
    activeListings: "Annonces actives",
    contactRequests: "Demandes de contact",
    loiReceived: "LOI reçues",
    totalViews: "Vues totales",
    overview: "Aperçu",
    myDeals: "Mes annonces",
    messages: "Messages",
    documents: "Documents",
    consultants: "Consultants",
    partners: "Partenaires",
    ourExperts: "Nos experts",
    pendingContactRequests: "Demandes en attente",
    buyersWaiting: "Acheteurs en attente de votre réponse",
    noPendingRequests: "Aucune demande en attente",
    recentLOI: "LOI récentes",
    newLOI: "Nouvelles lettres d'intention",
    noNewLOI: "Aucune nouvelle LOI",
    quickActions: "Actions rapides",
    listNewBusiness: "Publier une annonce",
    manageListings: "Gérer les annonces",
    viewMessages: "Voir les messages",
    getExpertHelp: "Aide d'expert",
    myBusinessListings: "Mes annonces",
    manageDeals: "Gérer vos annonces soumises",
    submitNewDeal: "Soumettre une annonce",
    noListingsYet: "Aucune annonce",
    submitFirstDeal: "Soumettre votre première annonce",
    askingPrice: "Prix demandé",
    views: "Vues",
    interestedBuyers: "Acheteurs intéressés",
    edit: "Modifier",
    viewPublicListing: "Voir l'annonce publique",
    deactivate: "Désactiver",
    messagesContactRequests: "Messages et demandes de contact",
    manageBuyerComms: "Gérer les communications acheteurs",
    noMessagesYet: "Aucun message",
    pendingRequests: "Demandes en attente",
    acceptedRequests: "Demandes acceptées",
    rejectedRequests: "Demandes refusées",
    viewProfile: "Voir le profil",
    accept: "Accepter",
    reject: "Refuser",
    scheduled: "Planifié",
    joinMeeting: "Rejoindre la réunion",
    rejected: "Refusé",
    documentsLOI: "Documents et LOI",
    allDocuments: "Tous vos documents et LOI reçues",
    lettersOfIntent: "Lettres d'intention",
    meetingRecordings: "Enregistrements de réunions",
    otherDocuments: "Autres documents",
    noLOIReceived: "Aucune LOI reçue",
    loiFrom: "LOI de",
    deal: "Affaire",
    offeredPrice: "Prix offert",
    receivedDate: "Date de réception",
    downloadLOI: "Télécharger la LOI",
    viewBuyer: "Voir l'acheteur",
    counterOffer: "Contre-offre",
    noMeetingRecordings: "Aucun enregistrement",
    watchRecording: "Voir l'enregistrement",
    viewTranscript: "Voir la transcription",
    download: "Télécharger",
    noOtherDocuments: "Aucun autre document",
    myConsultants: "Mes consultants",
    privateAdvisors: "Vos conseillers privés",
    noConsultantsAdded: "Aucun consultant ajouté",
    addConsultant: "Ajouter un consultant",
    myPartners: "Mes partenaires",
    internalExternal: "Partenaires internes et externes",
    noPartnersAdded: "Aucun partenaire ajouté",
    addPartner: "Ajouter un partenaire",
    internalPartners: "Partenaires internes",
    externalPartners: "Partenaires externes",
    acquizExperts: "Experts internes vérifiés d'AcquizGroup",
    noExpertsAvailable: "Aucun expert disponible",
    rate: "Tarif",
    availability: "Disponibilité",
    rating: "Note",
    reviews: "Avis",
    bookConsultation: "Réserver une consultation",
    message: "Message",
    expertCategories: "Catégories d'experts",
    browseExperts: "Parcourir par spécialité",
    businessLaw: "Droit des affaires",
    immigrationLaw: "Droit de l'immigration",
    cpaServices: "Services CPA",
    notaryPublic: "Notaire",
    taxAdvisory: "Conseil fiscal",
    experts: "Experts",
    partnersInvolved: "Partenaires impliqués",
    interestedIn: "Intéressé par",
    loading: "Chargement...",
    new: "Nouveau",
    respond: "Répondre",
    contact: "Contacter",
    acceptShareCalendar: "Accepter et partager le calendrier",
    uploadedAt: "Téléchargé le"
  }
};

// Types
interface Deal {
  id: string;
  businessName: string;
  businessDescription: string;
  industry: string;
  sector: string;
  askingPrice: number;
  currency: string;
  annualRevenue?: number;
  netProfit?: number;
  city: string;
  state: string;
  sellerId: string;
status: 'draft' | 'active' | 'under_review' | 'awaiting_seller_approval' | 'sold' | 'inactive';
  createdAt: any;
  updatedAt: any;
  viewCount?: number;
  interestedBuyers?: string[];
  // ── Champs de paiement, écrits par le webhook Stripe (dealSubmissions) ──
  // Utilisés comme filet de sécurité pour masquer la bannière CIM même si
  // le champ `status` n'a pas été remis à jour après paiement confirmé.
  submissionFeePaid?: boolean;
  approvalFeePaid?: boolean;
  // AJOUTÉ — écrit par sendForApproval() (deal-approval.service.ts) quand
  // le staff envoie le CIM au vendeur pour approbation. C'est le vrai
  // signal qui doit déclencher l'affichage de la bannière CIM — `status`
  // n'est jamais mis à 'awaiting_seller_approval' par sendForApproval(),
  // donc filtrer dessus ne fonctionnait jamais. Même champ déjà utilisé
  // correctement côté BrokerDashboard.tsx (pendingApprovalDeals).
  approvalPending?: boolean;
}

// ContactRequest type removed — now using NestJS API via SellerContactRequests component

interface MeetingDetails {
  id: string;
  scheduledAt: any;
  duration: number;
  meetingLink: string;
  calendarLink: string;
  attendees: string[];
  status: 'scheduled' | 'completed' | 'cancelled';
  recordingUrl?: string;
  transcriptUrl?: string;
}

interface LOIReceived {
  id: string;
  dealId: string;
  dealName: string;
  buyerId: string;
  buyerName: string;
  purchasePrice: number;
  currency: string;
  status: 'received' | 'reviewed' | 'accepted' | 'rejected' | 'negotiating';
  receivedAt: any;
  documentUrl: string;
}

interface Document {
  id: string;
  dealId: string;
  type: 'loi' | 'nda' | 'financial' | 'legal' | 'meeting_recording' | 'other';
  name: string;
  url: string;
  uploadedAt: any;
  uploadedBy: string;
  transcriptUrl?: string;
}

interface Partner {
  id: string;
  name: string;
  company: string;
  type: 'internal' | 'external';
  role: string;
  email: string;
  phone: string;
}

interface Expert {
  id: string;
  name: string;
  title: string;
  specialty: 'business_law' | 'immigration_law' | 'cpa' | 'notary' | 'tax_advisor';
  email: string;
  phone: string;
  bio: string;
  avatarUrl?: string;
  hourlyRate?: number;
  currency?: string;
  availability: string;
  isVerified: boolean;
  rating?: number;
  reviewCount?: number;
}

export default function SellerDashboard() {
  const { user } = useAuth();
  const userRegion = useUserRegion(user?.id);

  const navigate = useNavigate();
  const { toast } = useToast();

  // Language from global context
  const { language: lang } = useLanguage();
  const t = translations[lang];

  // State management
  const [activeTab, setActiveTab] = useState("overview");
  const [myDeals, setMyDeals] = useState<Deal[]>([]);
  const [contactRequestCount, setContactRequestCount] = useState(0);
  const [loiReceived, setLOIReceived] = useState<LOIReceived[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [experts, setExperts] = useState<Expert[]>([]);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Real-time notifications for new contact requests
  useContactRequestNotifications({ role: 'seller' });
  const [isLoading, setIsLoading] = useState(true);
  const [favoriteConsultants, setFavoriteConsultants] = useState<string[]>([]);

  // Toggle consultant favorite - persisted in Firestore
  const toggleConsultantFavorite = async (consultantId: string) => {
    if (!user) return;
    
    try {      
      const favRef = collection(db, "users", user.id, "consultantsFavorites");
      
      if (favoriteConsultants.includes(consultantId)) {
        const q = query(favRef, where("consultantId", "==", consultantId));
        const snapshot = await getDocs(q);
        snapshot.forEach(async (docSnap) => {
          await deleteDoc(docSnap.ref);
        });
        setFavoriteConsultants(prev => prev.filter(id => id !== consultantId));
      } else {
        await addDoc(favRef, { consultantId, addedAt: serverTimestamp() });
        setFavoriteConsultants(prev => [...prev, consultantId]);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  // Load favorites from Firestore
  useEffect(() => {
    if (!user) return;
    const loadFavorites = async () => {
      try {
        console.log('DEBUG user.id:', user.id, '| auth.currentUser.uid:', auth.currentUser?.uid);
        const favRef = collection(db, "users", user.id, "consultantsFavorites");
        const snapshot = await getDocs(favRef);
        setFavoriteConsultants(snapshot.docs.map(doc => doc.data().consultantId));
      } catch (error) {
        console.error("Error loading favorites:", error);
      }
    };
    loadFavorites();
  }, [user]);

  // Modal states
  const [showAddConsultant, setShowAddConsultant] = useState(false);
  const [showAddPartner, setShowAddPartner] = useState(false);

  useEffect(() => {
    if (user) {
      loadSellerDashboard();
    }
  }, [user]);

  const loadSellerDashboard = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Load seller's deals — deux requêtes séparées puis fusion, car
      // Firestore ne permet pas un OR direct entre deux champs différents.
      // 1) Deals que le vendeur a soumis lui-même (submittedBy).
      // 2) Deals soumis par SON broker en son nom (sellerId — voir
      //    deals.service.ts submitDeal(), qui y écrit representedSellerId).
      const dealsRef = collection(db, "dealSubmissions");
      const [submittedByMeSnap, representedByBrokerSnap] = await Promise.all([
        getDocs(query(dealsRef, where("submittedBy", "==", user.id))),
        getDocs(query(dealsRef, where("sellerId", "==", user.id))),
      ]);
      const dealsMap = new Map<string, Deal>();
      submittedByMeSnap.docs.forEach(doc => dealsMap.set(doc.id, { id: doc.id, ...doc.data() } as Deal));
      representedByBrokerSnap.docs.forEach(doc => dealsMap.set(doc.id, { id: doc.id, ...doc.data() } as Deal));
      const dealsData = Array.from(dealsMap.values());
      console.log("DEBUG dealsData.length:", dealsData.length);
      setMyDeals(dealsData);

      // Contact requests now handled by SellerContactRequests component via NestJS API
      // Just set a count for the KPI card
      try {
        const received = await contactRequestsApi.getReceived();
        setContactRequestCount(received.filter(r => r.status === 'pending').length);
        setUnreadMessages(received.filter(r => r.status === 'pending').length);
      } catch {
        setContactRequestCount(0);
      }

      // Load LOI received
      const loiRef = collection(db, "lois");
      const loiQuery = query(
        loiRef,
        where("sellerId", "==", user.id)
      );
      const loiSnapshot = await getDocs(loiQuery);
      const loiData = loiSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LOIReceived[];
      setLOIReceived(loiData);

      // Load documents
      const docsRef = collection(db, "sellerDocuments");
      const docsQuery = query(
        docsRef,
        where("sellerId", "==", user.id)
      );
      const docsSnapshot = await getDocs(docsQuery);
      const docsData = docsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Document[];
      setDocuments(docsData);

      // Load consultants
      const consultantsRef = collection(db, "sellerConsultants");
      const consultantsQuery = query(consultantsRef, where("sellerId", "==", user.id));
      const consultantsSnapshot = await getDocs(consultantsQuery);
      const consultantsData = consultantsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Consultant[];
      setConsultants(consultantsData);

      // Load partners
      const partnersRef = collection(db, "sellerPartners");
      const partnersQuery = query(partnersRef, where("sellerId", "==", user.id));
      const partnersSnapshot = await getDocs(partnersQuery);
      const partnersData = partnersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Partner[];
      setPartners(partnersData);

      // Load AcquizGroup experts
      const expertsRef = collection(db, "platformExperts");
      const expertsQuery = query(expertsRef, where("isActive", "==", true));
      const expertsSnapshot = await getDocs(expertsQuery);
      const expertsData = expertsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Expert[];
      setExperts(expertsData);

    } catch (error) {
      console.error("Error loading seller dashboard:", error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Accept/reject now handled by SellerContactRequests component via NestJS API

  // Add consultant
  const addConsultant = async (consultantData: Partial<Partner>) => {
    if (!user) return;

    try {
      await addDoc(collection(db, "sellerConsultants"), {
        ...consultantData,
        sellerId: user.id,
        addedAt: serverTimestamp()
      });

      toast({ title: lang === "en" ? "Consultant added" : "Consultant ajouté" });
      setShowAddConsultant(false);
      await loadSellerDashboard();
    } catch (error) {
      console.error("Error adding consultant:", error);
      toast({
        title: "Error",
        description: "Failed to add consultant",
        variant: "destructive"
      });
    }
  };

  // Remove consultant
  const removeConsultant = async (consultantId: string) => {
    try {
      await deleteDoc(doc(db, "sellerConsultants", consultantId));
      toast({ title: lang === "en" ? "Consultant removed" : "Consultant supprimé" });
      await loadSellerDashboard();
    } catch (error) {
      console.error("Error removing consultant:", error);
    }
  };

  // Add partner
  const addPartner = async (partnerData: Partial<Partner>) => {
    if (!user) return;

    try {
      await addDoc(collection(db, "sellerPartners"), {
        ...partnerData,
        sellerId: user.id,
        addedAt: serverTimestamp()
      });

      toast({ title: lang === "en" ? "Partner added" : "Partenaire ajouté" });
      setShowAddPartner(false);
      await loadSellerDashboard();
    } catch (error) {
      console.error("Error adding partner:", error);
    }
  };

  // Remove partner
  const removePartner = async (partnerId: string) => {
    try {
      await deleteDoc(doc(db, "sellerPartners", partnerId));
      toast({ title: lang === "en" ? "Partner removed" : "Partenaire supprimé" });
      await loadSellerDashboard();
    } catch (error) {
      console.error("Error removing partner:", error);
    }
  };

  // Utility functions
  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getSpecialtyLabel = (specialty: string) => {
    const labels: Record<string, { en: string; fr: string }> = {
      'business_law': { en: 'Business Law Attorney', fr: 'Avocat en droit des affaires' },
      'immigration_law': { en: 'Immigration Law Attorney', fr: 'Avocat en immigration' },
      'cpa': { en: 'Certified Public Accountant', fr: 'Comptable agréé' },
      'notary': { en: 'Notary Public', fr: 'Notaire' },
      'tax_advisor': { en: 'Tax Advisor', fr: 'Conseiller fiscal' }
    };
    return labels[specialty]?.[lang] || specialty;
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-background">
        <SellerSidebar activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="flex-1 flex flex-col">
          <DashboardHeader />
          <main className="flex-1 p-6 bg-muted/30 flex items-center justify-center">
            <p className="text-muted-foreground">{t.loading}</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <SellerSidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <div className="flex-1 flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-6 bg-muted/30">
          <div className="space-y-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>

        {/* CIM Approval Banners */}
        {/*
          CORRECTIF (5 août 2026) : la bannière ne doit plus s'afficher une
          fois le paiement d'approbation (2 449 $) confirmé, même si le champ
          `status` du deal n'a pas été remis à jour côté backend après le
          webhook Stripe. On se fie donc en plus au champ `approvalFeePaid`,
          écrit directement par le webhook au moment du paiement réussi —
          plus fiable que `status` seul et indépendant de la logique de
          progression du pipeline (`stage`).

          CORRIGÉ — le filtre se basait sur `d.status === 'awaiting_seller_approval'`,
          un statut que sendForApproval() (deal-approval.service.ts) n'écrit
          JAMAIS — il écrit seulement `approvalPending: true`. La condition
          n'était donc jamais vraie et la bannière ne s'affichait jamais
          automatiquement côté vendeur. Filtre maintenant aligné sur
          `approvalPending`, exactement comme BrokerDashboard.tsx le fait déjà
          pour sa propre bannière en lecture seule.
        */}
{myDeals
  .filter(d => (d as any).approvalPending && !d.approvalFeePaid)
  .map(deal => (
    <CIMApprovalBanner
      key={deal.id}
      dealId={deal.id}
      businessName={deal.businessName}
      onApproved={() => loadSellerDashboard()}
      onChangesRequested={() => loadSellerDashboard()}
    />
  ))
}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t.activeListings}</CardDescription>
              <CardTitle className="text-3xl">
                {myDeals.filter(d => d.status === 'active').length}
              </CardTitle>
            </CardHeader>
          </Card>
          
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Shield className="h-4 w-4 text-primary" />
                {lang === 'fr' ? 'NDA Signés' : 'NDAs Signed'}
              </CardDescription>
              <CardTitle className="text-3xl text-primary">
                {documents.filter(d => d.type === 'nda').length}
              </CardTitle>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t.contactRequests}</CardDescription>
              <CardTitle className="text-3xl flex items-center gap-2">
                {contactRequestCount}
                {contactRequestCount > 0 && (
                  <Badge variant="destructive" className="text-xs">!</Badge>
                )}
              </CardTitle>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t.loiReceived}</CardDescription>
              <CardTitle className="text-3xl flex items-center gap-2">
                {loiReceived.filter(l => l.status === 'received').length}
                {loiReceived.filter(l => l.status === 'received').length > 0 && (
                  <Badge variant="destructive" className="text-xs">!</Badge>
                )}
              </CardTitle>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>{t.totalViews}</CardDescription>
              <CardTitle className="text-3xl">
                {myDeals.reduce((acc, deal) => acc + (deal.viewCount || 0), 0)}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Main Tabs - Navigation controlled by sidebar */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <OnboardingGuide
              role="seller"
              language={lang as 'en' | 'fr'}
              onTabChange={setActiveTab}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pending Contact Requests — via NestJS API */}
              <SellerContactRequests language={lang} />

              {/* Recent LOI */}
              <Card>
                <CardHeader>
                  <CardTitle>{t.recentLOI}</CardTitle>
                  <CardDescription>{t.newLOI}</CardDescription>
                </CardHeader>
                <CardContent>
                  {loiReceived.filter(l => l.status === 'received').length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">
                      {t.noNewLOI}
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {loiReceived
                        .filter(l => l.status === 'received')
                        .slice(0, 3)
                        .map(loi => (
                          <div key={loi.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <p className="font-medium">{loi.buyerName}</p>
                              <p className="text-sm text-muted-foreground">{loi.dealName}</p>
                              <p className="text-sm font-semibold text-primary">
                                {formatCurrency(loi.purchasePrice, loi.currency)}
                              </p>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => window.open(loi.documentUrl, '_blank')}>
                              <Download className="h-4 w-4 mr-2" />
                              {t.downloadLOI}
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Compact Calendar Widget */}
            <CompactCalendarWidget />

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>{t.quickActions}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button onClick={() => setActiveTab("submit-deal")} variant="outline" className="h-24 flex flex-col">
                    <Upload className="h-6 w-6 mb-2" />
                    {t.listNewBusiness}
                  </Button>
                  <Button onClick={() => setActiveTab("my-deals")} variant="outline" className="h-24 flex flex-col">
                    <Building2 className="h-6 w-6 mb-2" />
                    {t.manageListings}
                  </Button>
                  <Button onClick={() => setActiveTab("messages")} variant="outline" className="h-24 flex flex-col relative">
                    <MessageSquare className="h-6 w-6 mb-2" />
                    {t.viewMessages}
                    {unreadMessages > 0 && (
                      <Badge className="absolute top-2 right-2">{unreadMessages}</Badge>
                    )}
                  </Button>
                  <Button onClick={() => setActiveTab("experts")} variant="outline" className="h-24 flex flex-col">
                    <UserCheck className="h-6 w-6 mb-2" />
                    {t.getExpertHelp}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Deals Tab */}
          <TabsContent value="my-deals" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t.myBusinessListings}</CardTitle>
                  <CardDescription>{t.manageDeals}</CardDescription>
                </div>
                <Button onClick={() => setActiveTab("submit-deal")}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t.submitNewDeal}
                </Button>
              </CardHeader>
              <CardContent>
                {myDeals.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">{t.noListingsYet}</p>
                    <Button onClick={() => setActiveTab("submit-deal")}>
                      {t.submitFirstDeal}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {myDeals.map(deal => (
                      <Card key={deal.id}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-lg">{deal.businessName}</CardTitle>
                            <Badge variant={
                              deal.status === 'active' ? 'default' :
                              deal.status === 'under_review' ? 'secondary' :
                              deal.status === 'sold' ? 'outline' : 'destructive'
                            }>
                              {deal.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          <CardDescription>{deal.businessDescription?.substring(0, 80)}...</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <p className="text-muted-foreground">{t.askingPrice}</p>
                              <p className="font-semibold">{formatCurrency(deal.askingPrice, deal.currency)}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">{t.views}</p>
                              <p className="font-semibold">{deal.viewCount || 0}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-muted-foreground">{t.interestedBuyers}</p>
                              <p className="font-semibold">{deal.interestedBuyers?.length || 0}</p>
                            </div>
                          </div>
                          {/* Bouton Approuver le CIM retiré de cette carte — l'approbation
                              se fait exclusivement via la bannière CIMApprovalBanner ci-dessus,
                              qui offre "Voir le CIM" avant d'approuver. Le bouton /deals/approve-cim
                              dupliquait sans garde-fou le mécanisme de seller-approve et pouvait
                              approuver un CIM jamais consulté. Voir audit — chantiers ouverts. */}
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/seller/edit-deal/${deal.id}`)}>
                              <Edit className="h-4 w-4 mr-1" />
                              {t.edit}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => navigate(`/deals/${deal.id}`)}>
                              <Eye className="h-4 w-4 mr-1" />
                              {t.viewPublicListing}
                            </Button>
                            <UpdateImpactDataDialog dealId={deal.id} />
                          </div>
                          <SecureDocumentsSection dealId={deal.id} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

         {/* Messages Tab */}
<TabsContent value="messages">
  <div style={{ height: 'calc(100vh - 200px)' }}>
    <SharedMessagesV2 role="seller" />
  </div>
</TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t.documentsLOI}
                </CardTitle>
                <CardDescription>{t.allDocuments}</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="loi">
                  <TabsList>
                    <TabsTrigger value="loi">
                      {t.lettersOfIntent}
                      {loiReceived.filter(l => l.status === 'received').length > 0 && (
                        <Badge className="ml-2">{loiReceived.filter(l => l.status === 'received').length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="meetings">{t.meetingRecordings}</TabsTrigger>
                    <TabsTrigger value="other">{t.otherDocuments}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="loi" className="space-y-4 mt-4">
                    {loiReceived.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">{t.noLOIReceived}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {loiReceived.map(loi => (
                          <Card key={loi.id}>
                            <CardHeader>
                              <div className="flex justify-between items-start">
                                <div>
                                  <CardTitle className="text-lg">{t.loiFrom} {loi.buyerName}</CardTitle>
                                  <CardDescription>{t.deal}: {loi.dealName}</CardDescription>
                                </div>
                                <Badge>{loi.status}</Badge>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                  <p className="text-sm text-muted-foreground">{t.offeredPrice}</p>
                                  <p className="font-semibold text-lg">{formatCurrency(loi.purchasePrice, loi.currency)}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">{t.receivedDate}</p>
                                  <p className="font-semibold">{loi.receivedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button variant="outline" onClick={() => window.open(loi.documentUrl, '_blank')}>
                                  <Download className="h-4 w-4 mr-2" />
                                  {t.downloadLOI}
                                </Button>
                                <Button variant="outline" onClick={() => navigate(`/buyer-profile/${loi.buyerId}`)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  {t.viewBuyer}
                                </Button>
                                {loi.status === 'received' && (
                                  <>
                                    <Button>
                                      <Check className="h-4 w-4 mr-2" />
                                      {t.accept}
                                    </Button>
                                    <Button variant="secondary">
                                      {t.counterOffer}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="meetings" className="space-y-4 mt-4">
                    {documents.filter(d => d.type === 'meeting_recording').length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">{t.noMeetingRecordings}</p>
                    ) : (
                      <div className="space-y-4">
                        {documents
                          .filter(d => d.type === 'meeting_recording')
                          .map(doc => (
                            <Card key={doc.id}>
                              <CardHeader>
                                <CardTitle className="text-lg">{doc.name}</CardTitle>
                                <CardDescription>
                                  {t.uploadedAt}: {doc.uploadedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                </CardDescription>
                              </CardHeader>
                              <CardContent className="flex gap-2">
                                <Button variant="outline" onClick={() => window.open(doc.url, '_blank')}>
                                  <Video className="h-4 w-4 mr-2" />
                                  {t.watchRecording}
                                </Button>
                                {doc.transcriptUrl && (
                                  <Button variant="outline" onClick={() => window.open(doc.transcriptUrl, '_blank')}>
                                    <FileText className="h-4 w-4 mr-2" />
                                    {t.viewTranscript}
                                  </Button>
                                )}
                                <Button variant="outline" onClick={() => window.open(doc.url, '_blank')}>
                                  <Download className="h-4 w-4 mr-2" />
                                  {t.download}
                                </Button>
                              </CardContent>
                            </Card>
                          ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="other" className="space-y-4 mt-4">
                    {documents.filter(d => d.type !== 'meeting_recording' && d.type !== 'loi').length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">{t.noOtherDocuments}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {documents
                          .filter(d => d.type !== 'meeting_recording' && d.type !== 'loi')
                          .map(doc => (
                            <Card key={doc.id}>
                              <CardContent className="pt-6 flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{doc.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {doc.type} • {doc.uploadedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                  </p>
                                </div>
                                <Button variant="outline" onClick={() => window.open(doc.url, '_blank')}>
                                  <Download className="h-4 w-4 mr-2" />
                                  {t.download}
                                </Button>
                              </CardContent>
                            </Card>
                          ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consultants Tab */}
          <TabsContent value="consultants" className="space-y-4">
            <ConsultantsTab
              consultants={consultants}
              favoriteConsultants={favoriteConsultants}
              onAddClick={() => setShowAddConsultant(true)}
              onRemove={removeConsultant}
              onToggleFavorite={toggleConsultantFavorite}
              language={lang}
              formatCurrency={formatCurrency}
              gateContactTier={myDeals.some(d => d.status !== 'draft') ? undefined : "essential"}
            />
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners" className="space-y-4">
            <PartnersTab
              partners={partners}
              consultants={consultants}
              favoriteConsultants={favoriteConsultants}
              onAddClick={() => setShowAddPartner(true)}
              onRemove={removePartner}
              onToggleFavorite={toggleConsultantFavorite}
              onViewConsultants={() => setActiveTab('consultants')}
              language={lang}
              formatCurrency={formatCurrency}
            />
          </TabsContent>

          {/* Experts Tab */}
          <TabsContent value="experts" className="space-y-4">
            <ExpertsTab
              experts={experts}
              language={lang}
              userRegion={userRegion}
              formatCurrency={formatCurrency}
            />
          </TabsContent>


          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-4">
            <CalendarTab showAvailabilitySettings={true} />
          </TabsContent>

          {/* Submit Deal Tab */}
          <TabsContent value="submit-deal" className="space-y-4">
            <SubmitDealForm onComplete={() => setActiveTab("overview")} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Consultant Modal */}
      {showAddConsultant && (
        <AddConsultantModal
          onClose={() => setShowAddConsultant(false)}
          onAdd={addConsultant}
          lang={lang}
        />
      )}

      {/* Add Partner Modal */}
      {showAddPartner && (
        <AddPartnerModal
          onClose={() => setShowAddPartner(false)}
          onAdd={addPartner}
          lang={lang}
        />
      )}
          </div>
        </main>
      </div>
    </div>
  );
}

// Add Consultant Modal Component
function AddConsultantModal({
  onClose,
  onAdd,
  lang
}: {
  onClose: () => void;
  onAdd: (data: Partial<Partner>) => void;
  lang: "en" | "fr";
}) {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    role: "",
    email: "",
    phone: "",
    type: "external" as const
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{lang === "en" ? "Add Consultant" : "Ajouter un consultant"}</CardTitle>
          <CardDescription>
            {lang === "en" ? "Add a professional advisor to your team" : "Ajoutez un conseiller à votre équipe"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{lang === "en" ? "Full Name" : "Nom complet"} *</label>
              <input
                className="w-full border rounded-md p-2"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{lang === "en" ? "Company" : "Entreprise"}</label>
              <input
                className="w-full border rounded-md p-2"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{lang === "en" ? "Role/Specialty" : "Rôle/Spécialité"} *</label>
              <input
                className="w-full border rounded-md p-2"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email *</label>
              <input
                type="email"
                className="w-full border rounded-md p-2"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{lang === "en" ? "Phone" : "Téléphone"}</label>
              <input
                className="w-full border rounded-md p-2"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {lang === "en" ? "Cancel" : "Annuler"}
              </Button>
              <Button type="submit">
                {lang === "en" ? "Add Consultant" : "Ajouter"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Add Partner Modal Component
function AddPartnerModal({
  onClose,
  onAdd,
  lang
}: {
  onClose: () => void;
  onAdd: (data: Partial<Partner>) => void;
  lang: "en" | "fr";
}) {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    type: "external" as 'internal' | 'external',
    role: "",
    email: "",
    phone: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{lang === "en" ? "Add Partner" : "Ajouter un partenaire"}</CardTitle>
          <CardDescription>
            {lang === "en" ? "Add an internal or external business partner" : "Ajoutez un partenaire interne ou externe"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{lang === "en" ? "Partner Type" : "Type de partenaire"} *</label>
              <select
                className="w-full border rounded-md p-2"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'internal' | 'external' })}
                required
              >
                <option value="internal">{lang === "en" ? "Internal (AcquizGroup Platform)" : "Interne (Plateforme AcquizGroup)"}</option>
                <option value="external">{lang === "en" ? "External (Third Party)" : "Externe (Tiers)"}</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{lang === "en" ? "Full Name" : "Nom complet"} *</label>
              <input
                className="w-full border rounded-md p-2"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{lang === "en" ? "Company" : "Entreprise"} *</label>
              <input
                className="w-full border rounded-md p-2"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{lang === "en" ? "Role" : "Rôle"} *</label>
              <input
                className="w-full border rounded-md p-2"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email *</label>
              <input
                type="email"
                className="w-full border rounded-md p-2"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{lang === "en" ? "Phone" : "Téléphone"}</label>
              <input
                className="w-full border rounded-md p-2"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {lang === "en" ? "Cancel" : "Annuler"}
              </Button>
              <Button type="submit">
                {lang === "en" ? "Add Partner" : "Ajouter"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}